import { useLanguage } from "@/contexts/LanguageContext";
import { Shield, Globe, Code, Zap } from "lucide-react";
import { motion } from "framer-motion";

const AboutSection = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />,
      title: t("digital_sovereignty"),
      description: t("digital_sovereignty_desc"),
      gradient: "from-blue-600/5 via-blue-500/5 to-transparent"
    },
    {
      icon: <Globe className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />,
      title: t("regulated_sectors"),
      description: t("regulated_sectors_desc"),
      gradient: "from-emerald-600/5 via-emerald-500/5 to-transparent"
    },
    {
      icon: <Code className="w-8 h-8 text-blue-500 dark:text-blue-400" />,
      title: t("open_innovation"),
      description: t("open_innovation_desc"),
      gradient: "from-blue-500/5 via-blue-400/5 to-transparent"
    },
    {
      icon: <Zap className="w-8 h-8 text-emerald-500 dark:text-emerald-400" />,
      title: t("democratizing_ai"),
      description: t("democratizing_ai_desc"),
      gradient: "from-emerald-500/5 via-emerald-400/5 to-transparent"
    }
  ];

  return (
    <section id="about" className="relative overflow-hidden py-24 sm:py-28">
      <div className="absolute inset-0 z-0">
        <div className="absolute right-1/4 top-1/4 h-96 w-96 rounded-full bg-blue-500/5 blur-[120px] dark:bg-blue-600/5"></div>
        <div className="absolute bottom-1/4 left-1/4 h-96 w-96 rounded-full bg-slate-500/5 blur-[120px] dark:bg-slate-600/5"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mx-auto mb-14 max-w-4xl text-center"
        >
          <h2 className="section-heading mb-6">
            {t("about_title")}
          </h2>
          <p className="section-subtitle mx-auto max-w-3xl">
            {t("company_desc")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2 xl:grid-cols-4"
        >
          {features.map((feature, index) => (
            <div
              key={index}
              className="soft-card group relative overflow-hidden"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100`} />
              <div className="relative">
                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100/90 dark:bg-slate-900/90">
                  {feature.icon}
                </div>
                <h3 className="mb-3 text-xl font-semibold text-slate-900 transition-colors group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
                  {feature.title}
                </h3>
                <p className="text-sm leading-7 text-slate-600 dark:text-slate-400">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
